import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { JobberPicker } from '@/components/jobbers/JobberPicker';
import { DateField } from '@/components/tissue/DateField';
import { joApi, joErr } from '@/services/processingJoApi';
import { FgPicker } from './FgPicker';
import { RouteOptions } from './JoRoute';

const PU = 'Processing Unit';
const MODES = [['jobber', 'A specific jobber'], ['category', 'A category (pick the jobber at Assign Unit)']];

const SendTo = ({ mode, setMode, jobber, setJobber, category, setCategory, categories }) => (
  <div className="space-y-2">
    <Label>Send to *</Label>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="jo-sendto-mode">
      {MODES.map(([v, l]) => (
        <button key={v} type="button" onClick={() => setMode(v)} data-testid={`jo-sendto-${v}`}
          className={`rounded-md border px-3 py-2 text-left text-sm transition-colors ${mode === v ? 'border-[#2D5F4F] bg-[#2D5F4F]/5 text-[#2D5F4F] font-medium' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>{l}</button>
      ))}
    </div>
    {mode === 'jobber' && <JobberPicker value={jobber} onChange={setJobber} testId="jo-jobber" preferCategory={PU} />}
    {mode === 'category' && (
      <Select value={category} onValueChange={setCategory}>
        <SelectTrigger data-testid="jo-category"><SelectValue placeholder="Pick a category" /></SelectTrigger>
        <SelectContent>{categories.map((c) => <SelectItem key={c} value={c} data-testid={`jo-category-${c.replace(/\s+/g, '-').toLowerCase()}`}>{c}</SelectItem>)}</SelectContent>
      </Select>
    )}
  </div>
);

export const NewJoDialog = ({ open, onOpenChange, onCreated }) => {
  const [fg, setFg] = useState(null);
  const [pieces, setPieces] = useState('');
  const [mode, setMode] = useState('category');
  const [jobber, setJobber] = useState('');
  const [category, setCategory] = useState(PU);
  const [categories, setCategories] = useState([PU]);
  const [target, setTarget] = useState('');
  const [remarks, setRemarks] = useState('');
  const [route, setRoute] = useState('INHOUSE');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setFg(null); setPieces(''); setMode('category'); setJobber(''); setCategory(PU); setTarget(''); setRemarks(''); setRoute('INHOUSE'); joApi.meta().then((m) => setCategories(m.categories || [PU])).catch(() => {}); } }, [open]);
  const ready = fg && Number(pieces) > 0 && (mode === 'jobber' ? jobber : category);

  const submit = async () => {
    setSaving(true);
    try {
      const o = await joApi.create({ fg_name: fg.fg_name, pieces: Number(pieces), send_to_type: mode, jobber_name: mode === 'jobber' ? jobber : null,
        category: mode === 'category' ? category : null, target_date: target || null, remarks, route });
      toast.success(`Job order ${o.jo_id} sent to Greige Storage`);
      onOpenChange(false); onCreated?.(o);
    } catch (e) { toast.error(joErr(e, 'Could not raise the job order')); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="new-jo-dialog">
        <DialogHeader>
          <DialogTitle>New Processing job order</DialogTitle>
          <DialogDescription>Greige Storage picks the base articles from stock in hand — you can raise the order even when stock is short.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>FG Name *</Label><FgPicker value={fg} onPick={setFg} />
            {fg && <p className="text-[11px] text-gray-500" data-testid="jo-fg-info">{fg.base_articles} base article(s) · {fg.free_pcs} pcs free in Greige Storage right now</p>}</div>
          <div className="space-y-1.5"><Label>Pieces *</Label>
            <Input type="number" min="1" inputMode="numeric" value={pieces} onChange={(e) => setPieces(e.target.value)} placeholder="Pieces to process" data-testid="jo-pieces" /></div>
          <SendTo {...{ mode, setMode, jobber, setJobber, category, setCategory, categories }} />
          <div className="space-y-2"><Label>Route after the jobber *</Label><RouteOptions value={route} onChange={setRoute} /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Expected delivery date</Label><DateField value={target} onChange={setTarget} testId="jo-target-date" /></div>
            <div className="space-y-1.5"><Label>Remarks</Label><Input value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="jo-remarks" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="jo-cancel-btn">Cancel</Button>
          <Button disabled={!ready || saving} onClick={submit} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="jo-submit-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Raise job order</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewJoDialog;
